/**
 * @file 課題に口頭で取り組む対話を出す。
 *
 * @what: 相手役の一言を毎回その場で作り、音で流す。本人が話して返すと、書き起こし・届いた段・
 *   直すところ・次の一言を出す。やりとりは続けられ、続けるたびに相手役の一言が入れ替わる。
 * @why: 技能の到達は本を読んだかでは測れないので、場面を決めて話させ、その場で評価する
 *   (設計 plan/docs/study_skill_task_design.md §5)。**一言の文字は最初は隠す。** ※なぜなら
 *   先に読めると聞き取りの練習にならないためである。聞き取れないときだけ開ける。
 * @failure: 一言を作れないときはカリキュラムに書いてある一言に落とす。マイクを使えないとき、
 *   AI が呼べないときは1行で知らせる。音声は送ったあと画面にも残さない。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  evaluateSpeak,
  speakInputFrom,
  speakNextFocus,
  understandingLabel,
  type StudySpeakResult,
} from "../api/speak";
import { makeTaskOpening, taskOpeningInputFrom } from "../api/taskOpening";
import type { StudyTextbookRouteTask } from "../api/textbookRoutes";
import { useLineSpeech } from "../hooks/useLineSpeech";
import { useSpeakRecorder } from "../hooks/useSpeakRecorder";
import { StudyDialog } from "./StudyDialog";
import "./study.css";

/** やりとり1往復の記録 (画面の中だけで持つ)。 */
interface Exchange {
  /** 相手役が言ったこと。 */
  said: string;
  /** 本人が返したこと (書き起こし)。 */
  replied: string;
  result: StudySpeakResult;
}

export function SpeakTaskDialog({
  task,
  markLabels,
  familyId,
  learnerPersonId,
  onClose,
}: {
  task: StudyTextbookRouteTask;
  /** 目盛りの値から名前を引く表 (ルートの scale_marks から作る)。 */
  markLabels: ReadonlyMap<number, string>;
  familyId: string;
  learnerPersonId: string;
  onClose: () => void;
}) {
  const recorder = useSpeakRecorder();
  const speech = useLineSpeech(familyId, learnerPersonId);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  // その回の1往復目の一言が入る。開いたときに作る。
  const [opening, setOpening] = useState<string | null>(null);
  const [shown, setShown] = useState(false);
  // 作ったことがあるかを持つ (作り直しを1回に閉じる)。
  const madeRef = useRef(false);

  // いま相手役が言っていること。1往復目は作った一言、2往復目以降は前の返しである。
  const last = exchanges[exchanges.length - 1];
  const said = last?.result.next_line ?? opening ?? "";

  // 閉じるときに録音を必ず止める (マイクを開いたままにしない)。
  useEffect(() => () => recorder.cancel(), [recorder]);

  // 開いたときに一言を作る。作れなければカリキュラムに書いてある一言に落とす。
  useEffect(() => {
    if (madeRef.current) return;
    madeRef.current = true;
    let alive = true;
    void (async () => {
      try {
        const res = await makeTaskOpening(
          familyId,
          learnerPersonId,
          taskOpeningInputFrom(task, []),
        );
        if (alive) setOpening(res.line);
      } catch (err) {
        console.warn("[study] failed to make the opening line, falling back", err);
        if (alive) setOpening(task.opening ?? "");
      }
    })();
    return () => {
      alive = false;
    };
  }, [familyId, learnerPersonId, task]);

  // 返しのたびに文字は隠し直す (次の一言も聞いて取る)。
  useEffect(() => setShown(false), [said]);

  const send = useCallback(async () => {
    const rec = await recorder.stop();
    if (rec === null) return;
    setSending(true);
    setSendError(null);
    try {
      const input = speakInputFrom(task, markLabels, rec.base64, rec.mime, said);
      const result = await evaluateSpeak(familyId, learnerPersonId, input);
      setExchanges((prev) => [...prev, { said, replied: result.transcript, result }]);
    } catch (err) {
      console.error("[study] failed to evaluate the spoken answer", err);
      setSendError("評価できませんでした。時間を置いてもう一度お試しください。");
    } finally {
      setSending(false);
    }
  }, [recorder, task, markLabels, said, familyId, learnerPersonId]);

  const recording = recorder.state === "recording";
  const busy = sending || recorder.state === "encoding";

  return (
    <StudyDialog title={task.label} labelledBy="study-speak-dialog-title" onClose={onClose}>
      <p className="study-speak-scene">{task.scene}</p>
      <p className="study-muted">相手役: {task.role}</p>

      {said ? (
        <>
          {/* 文字は最初は隠す。先に読めると聞き取りの練習にならない。 */}
          <p className="study-speak-said">
            <span className="study-speak-said-label">相手</span>
            {shown ? said : <span className="study-muted">聞いてから答えます</span>}
          </p>
          <div className="study-speak-listen">
            <button
              type="button"
              className="study-primary-btn"
              onClick={() => void speech.play(said)}
              disabled={speech.state === "loading"}
            >
              聞く
            </button>
            <button
              type="button"
              className="study-ghost-btn"
              onClick={() => void speech.play(said, true)}
              disabled={speech.state === "loading"}
            >
              ゆっくり聞く
            </button>
            {!shown ? (
              <button type="button" className="study-ghost-btn" onClick={() => setShown(true)}>
                文字で見る
              </button>
            ) : null}
            {speech.state === "loading" ? (
              <span role="status" className="study-muted">
                読み上げています...
              </span>
            ) : null}
          </div>
        </>
      ) : opening === null ? (
        <p role="status" className="study-muted">
          相手役の一言を用意しています...
        </p>
      ) : (
        <p className="study-muted">先に話しかけてみてください。</p>
      )}

      {speech.error !== null ? (
        <p role="alert" className="study-error">
          {speech.error}
        </p>
      ) : null}
      {recorder.error !== null ? (
        <p role="alert" className="study-error">
          {recorder.error}
        </p>
      ) : null}
      {sendError !== null ? (
        <p role="alert" className="study-error">
          {sendError}
        </p>
      ) : null}

      <div className="study-speak-actions">
        {recording ? (
          <button type="button" className="study-primary-btn" onClick={() => void send()}>
            話し終わった
          </button>
        ) : (
          <button
            type="button"
            className="study-primary-btn"
            onClick={() => void recorder.start()}
            disabled={busy}
          >
            {exchanges.length === 0 ? "話して返す" : "続けて返す"}
          </button>
        )}
        {recording ? (
          <span role="status" className="study-speak-recording">
            録音しています
          </span>
        ) : null}
        {busy ? (
          <span role="status" className="study-muted">
            評価しています...
          </span>
        ) : null}
      </div>

      <p className="study-muted study-speak-note">
        音声は評価に使ったあと捨てられ、どこにも保存されません。
      </p>

      {exchanges.map((x, i) => (
        <section key={i} className="study-speak-result">
          <h4 className="study-speak-result-title">{i + 1}回目</h4>
          <p className="study-speak-transcript">{x.replied}</p>
          {/* 聞き取りと返しを分けて出す。届いた段だけでは、どちらで詰まったかが分からない。 */}
          <dl className="study-speak-axes">
            <div className="study-speak-axis">
              <dt>聞き取り</dt>
              <dd>
                {x.result.understanding !== undefined ? (
                  <>
                    <b>{understandingLabel(x.result.understanding)}</b>
                    {x.result.understanding_note ? (
                      <span className="study-muted">{x.result.understanding_note}</span>
                    ) : null}
                  </>
                ) : (
                  <span className="study-muted">判定できませんでした</span>
                )}
              </dd>
            </div>
            <div className="study-speak-axis">
              <dt>返し</dt>
              <dd>
                {x.result.reached_mark > 0 ? (
                  <>
                    <b>{x.result.reached_label ?? x.result.reached_mark}</b>まで届いています
                  </>
                ) : (
                  <>どの段にも届いていません</>
                )}
                {x.result.reason ? <span className="study-muted">{x.result.reason}</span> : null}
              </dd>
            </div>
          </dl>
          {speakNextFocus(x.result) !== null ? (
            <p className="study-speak-focus">{speakNextFocus(x.result)}</p>
          ) : null}
          {x.result.fixes.length > 0 ? (
            <ul className="study-speak-fixes" aria-label="直すところ">
              {x.result.fixes.map((f, j) => (
                <li key={j}>{f}</li>
              ))}
            </ul>
          ) : null}
        </section>
      ))}
    </StudyDialog>
  );
}
